import {
  Box,
  Container,
  Divider,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { motion, Variants } from "framer-motion";

import SecurityIcon from "@mui/icons-material/Security";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import CookieIcon from "@mui/icons-material/Cookie";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import PageMeta from '../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

const sections = [
  {
    icon: <SecurityIcon fontSize="small" />,
    title: "Information we collect",
    body: `When you create an account or use The Quizzler, we may collect information such as your username, email address, quiz activity, leaderboard statistics, and basic technical information like browser type or IP address. This helps us operate the site, prevent abuse, and improve the experience for players.`,
  },
  {
    icon: <AnalyticsIcon fontSize="small" />,
    title: "How we use information",
    body: `We use collected information to provide core site features such as authentication, score tracking, leaderboards, account management, analytics, and security monitoring. We may also use anonymous or aggregated data to improve gameplay balance, question quality, and site performance.`,
  },
  {
    icon: <CookieIcon fontSize="small" />,
    title: "Cookies & analytics",
    body: `The Quizzler may use cookies, local storage, and analytics tools to keep you logged in, remember preferences, understand site usage, and improve reliability. Third-party services such as analytics or advertising providers may also place cookies in accordance with their own policies.`,
  },
  {
    icon: <DeleteOutlineIcon fontSize="small" />,
    title: "Your choices",
    body: `You may request account deletion or contact us with questions about your data at any time. While we take reasonable steps to protect user information, no online service can guarantee absolute security.`,
  },
  {
    icon: <EmailOutlinedIcon fontSize="small" />,
    title: "Contact",
    body: `If you have questions about this Privacy Policy, spot a security issue, or need help with your account, please contact us through the site's contact page or support email.`,
  },
];

export default function PrivacyPage() {
  return (
    <>
      <PageMeta
        title="Privacy Policy | The Quizzler"
        description="Read The Quizzler's privacy policy to understand how we handle your data on our fan-made Harry Potter trivia site."
        canonical="https://thequizzler.app/privacy"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{ letterSpacing: "-0.5px" }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            How The Quizzler collects, uses, and protects information.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Last updated: May 13, 2026
          </Typography>

          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Sections */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            mb: 5,
          }}
        >
          {sections.map((section, i) => (
            <motion.div
              key={section.title}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    mt: "2px",
                    color: "primary.main",
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    bgcolor: "#5c6bc0",
                    "& svg": { color: "white" },
                  }}
                >
                  {section.icon}
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                  >
                    {section.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                  >
                    {section.body}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* Legal Notice */}
        <motion.div
          custom={sections.length}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "action.hover",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <GavelIcon fontSize="small" color="action" />

              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
              >
                Privacy Notice
              </Typography>

              <Chip
                label="Fan Project"
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.8 }}
            >
              The Quizzler is an independent fan-made trivia site created for
              entertainment purposes. We aim to collect as little personal
              information as reasonably possible while still providing account,
              leaderboard, and gameplay functionality.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.8, mt: 1.5 }}
            >
              This Privacy Policy may be updated periodically as the site evolves,
              introduces new features, or integrates additional third-party
              services such as analytics or advertising providers.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </>
  );
}